import React, { useState } from 'react';
import Button from '../common/Button';
import ImageViewerModal from '../common/ImageViewerModal';
import ImageCarousel from '../common/ImageCarousel';

// Responsive hook
function useIsMobile() {
    const [isMobile, setIsMobile] = React.useState(window.innerWidth < 640);
    React.useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return isMobile;
}

const BeforeAfterImageCard = ({ imageData, onDelete }) => {
    const [showBeforeImage, setShowBeforeImage] = useState(false);
    const [showAfterImage, setShowAfterImage] = useState(false);
    const isMobile = useIsMobile();

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleDelete = () => {
        onDelete(imageData);
    };

    const handleImageClick = (image, index) => {
        if (index === 0) {
            setShowBeforeImage(true);
        } else {
            setShowAfterImage(true);
        }
    };

    // Prepare images for carousel
    const carouselImages = [
        {
            url: imageData.beforeImageUrl,
            alt: 'Before treatment',
            type: 'before',
            date: imageData.beforeServiceDate
        },
        {
            url: imageData.afterImageUrl,
            alt: 'After treatment',
            type: 'after',
            date: imageData.afterServiceDate
        }
    ];

    return (
        <>
            <div className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                <div className="p-4">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-0 mb-4">
                        <div className="flex-1">
                            <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-1">
                                Before & After Images
                            </h4>
                            <p className="text-sm text-gray-500">
                                {imageData.description || 'Patient transformation'}
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                            {imageData.patientConsent && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Consent Given
                                </span>
                            )}
                            {imageData.isVerified && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Verified
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Images: Carousel on mobile, grid on desktop */}
                    <div className="mb-4">
                        {isMobile ? (
                            <ImageCarousel
                                images={carouselImages}
                                onImageClick={handleImageClick}
                                showNavigation={true}
                                autoSlide={false}
                            />
                        ) : (
                            <div className="grid grid-cols-2 gap-4">
                                {/* Before Image */}
                                <div className="space-y-2">
                                    <div className="text-center">
                                        <span className="text-sm font-medium text-red-600">Before</span>
                                    </div>
                                    <div 
                                        className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => handleImageClick(carouselImages[0], 0)}
                                    >
                                        <img
                                            src={imageData.beforeImageUrl}
                                            alt="Before treatment"
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCAxMDBDNjAgNzcuOTA4NiA3Ny45MDg2IDYwIDEwMCA2MEMxMjIuMDkxIDYwIDE0MCA3Ny45MDg2IDE0MCAxMDBDMTQwIDEyMi4wOTEgMTIyLjA5MSAxNDAgMTAwIDE0MEM3Ny45MDg2IDE0MCA2MCAxMjIuMDkxIDYwIDEwMFoiIGZpbGw9IiNEMUQ1REIiLz4KPHBhdGggZD0iTTEwMCAxMjBDMTEwLjQ1NyAxMjAgMTE5IDExMS40NTcgMTE5IDEwMUMxMTkgOTAuNTQzNCAxMTAuNDU3IDgyIDEwMCA4MkM4OS41NDM0IDgyIDgxIDkwLjU0MzQgODEgMTAxQzgxIDExMS40NTcgODkuNTQzNCAxMjAgMTAwIDEyMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                                            }}
                                        />
                                    </div>
                                    <div className="text-center">
                                        <span className="text-xs text-gray-500">
                                            {formatDate(imageData.beforeServiceDate)}
                                        </span>
                                    </div>
                                </div>
                                {/* After Image */}
                                <div className="space-y-2">
                                    <div className="text-center">
                                        <span className="text-sm font-medium text-green-600">After</span>
                                    </div>
                                    <div 
                                        className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => handleImageClick(carouselImages[1], 1)}
                                    >
                                        <img
                                            src={imageData.afterImageUrl}
                                            alt="After treatment"
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCAxMDBDNjAgNzcuOTA4NiA3Ny45MDg2IDYwIDEwMCA2MEMxMjIuMDkxIDYwIDE0MCA3Ny45MDg2IDE0MCAxMDBDMTQwIDEyMi4wOTEgMTIyLjA5MSAxNDAgMTAwIDE0MEM3Ny45MDg2IDE0MCA2MCAxMjIuMDkxIDYwIDEwMFoiIGZpbGw9IiNEMUQ1REIiLz4KPHBhdGggZD0iTTEwMCAxMjBDMTEwLjQ1NyAxMjAgMTE5IDExMS40NTcgMTE5IDEwMUMxMTkgOTAuNTQzNCAxMTAuNDU3IDgyIDEwMCA4MkM4OS41NDM0IDgyIDgxIDkwLjU0MzQgODEgMTAxQzgxIDExMS40NTcgODkuNTQzNCAxMjAgMTAwIDEyMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                                            }}
                                        />
                                    </div>
                                    <div className="text-center">
                                        <span className="text-xs text-gray-500">
                                            {formatDate(imageData.afterServiceDate)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Image Labels (for mobile carousel only) */}
                        {isMobile && (
                            <div className="flex justify-between mt-2 text-xs text-gray-500">
                                <span className="text-red-600 font-medium">Before: {formatDate(imageData.beforeServiceDate)}</span>
                                <span className="text-green-600 font-medium">After: {formatDate(imageData.afterServiceDate)}</span>
                            </div>
                        )}
                    </div>

                    {/* Details */}
                    <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Treatment Period:</span>
                            <span className="text-gray-900">
                                {formatDate(imageData.beforeServiceDate)} - {formatDate(imageData.afterServiceDate)}
                            </span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Created:</span>
                            <span className="text-gray-900">{formatDate(imageData.createdAt)}</span>
                        </div>
                    </div>

                    {/* Action Button */}
                    <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={handleDelete}
                        className="w-full"
                    >
                        Delete Images
                    </Button>
                </div>
            </div>

            {/* Image Viewer Modals */}
            <ImageViewerModal
                isOpen={showBeforeImage}
                onClose={() => setShowBeforeImage(false)}
                imageUrl={imageData.beforeImageUrl}
                altText="Before treatment - Full size"
            />

            <ImageViewerModal
                isOpen={showAfterImage}
                onClose={() => setShowAfterImage(false)}
                imageUrl={imageData.afterImageUrl}
                altText="After treatment - Full size"
            />
        </>
    );
};

export default BeforeAfterImageCard; 